import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { apiClient } from '../../../api/axios';

const fetchDeliveryAttempts = async ({ queryKey }) => {
  const [, { eventId }] = queryKey;
  const { data } = await apiClient.get(`/events/${eventId}/delivery-attempts`);
  return data;
};

/**
 * Hook to fetch delivery attempts for an event
 * @param {string} eventId - The event ID
 * @returns {Object} Delivery attempts data and loading/error states
 */
export const useGetDeliveryAttemptsHandler = ({ eventId }) => {
  const dispatch = useDispatch();

  const {
    data: attemptsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['deliveryAttempts', { eventId }],
    queryFn: fetchDeliveryAttempts,
    enabled: !!eventId,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Handle errors
  useEffect(() => {
    if (isError && error instanceof AxiosError) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch delivery attempts';
      if (error.response?.status !== 404) {
        // Don't show error for 404, let the component handle it
        dispatch(showSnackbar({ message: errorMessage, severity: 'error' }));
      }
    }
  }, [isError, error, dispatch]);

  // Sort attempts by attempt_no descending (latest first)
  const attempts = attemptsData?.attempts || [];
  const sortedAttempts = [...attempts].sort((a, b) => {
    const attemptNoA = a.attempt_no || 0;
    const attemptNoB = b.attempt_no || 0;
    return attemptNoB - attemptNoA;
  });

  return {
    attempts: sortedAttempts,
    isLoading,
    isError,
    error,
    refetch,
  };
};
